import React, { useState, useEffect } from "react";
import { keyframes, styled } from "@mui/material/styles";
import MuiAppBar from "@mui/material/AppBar";
import { NavLink as RouterLink, useLocation } from "react-router-dom";
import { Icon } from "@iconify/react";

import Box from "@mui/material/Box";
import MuiDrawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import CssBaseline from "@mui/material/CssBaseline";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";

import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import VerifiedUserRoundedIcon from "@mui/icons-material/VerifiedUserRounded";
import { Stack } from "@mui/system";

import { navConfig } from "./NavConfig";
import AccountPopover from "../AccountPopover";
import ColorModeToggle from "../ColorModeToggle";
import { StyledNavItemIcon } from "./styles";
import "./style.css";

const drawerWidth = 290;
const topbarHeight = 72;

const packetFlow = keyframes`
  0% { offset-distance: 0%; opacity: 0; transform: scale(.75); }
  12% { opacity: 1; transform: scale(1); }
  88% { opacity: 1; transform: scale(1); }
  100% { offset-distance: 100%; opacity: 0; transform: scale(.75); }
`;

const dashFlow = keyframes`
  to { stroke-dashoffset: -28; }
`;

const rotateHub = keyframes`
  to { transform: rotate(360deg); }
`;

const hubPulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(49,84,186,.14); }
  50% { box-shadow: 0 0 0 8px rgba(49,84,186,0); }
`;

const contextEnter = keyframes`
  from { opacity: 0; transform: translateY(12px) scale(.97); filter: blur(3px); }
  to { opacity: 1; transform: translateY(0) scale(1); filter: blur(0); }
`;

const floatElement = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-6px); }
`;

const reportTravel = keyframes`
  0% { transform: translate(-50px, -6px) rotate(-8deg); opacity: 0; }
  20%, 75% { opacity: 1; }
  100% { transform: translate(52px, 48px) rotate(6deg); opacity: 0; }
`;

const barRise = keyframes`
  0%, 15% { transform: scaleY(.15); opacity: .35; }
  55%, 100% { transform: scaleY(1); opacity: 1; }
`;

const coinDrop = keyframes`
  0% { transform: translateY(-22px) rotateY(0); opacity: 0; }
  20%, 75% { opacity: 1; }
  100% { transform: translateY(55px) rotateY(360deg); opacity: 0; }
`;

const secureSweep = keyframes`
  0% { transform: translateX(-44px); opacity: 0; }
  20%, 75% { opacity: 1; }
  100% { transform: translateX(44px); opacity: 0; }
`;

const softPulse = keyframes`
  0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(0, 179, 134, .2); }
  50% { transform: scale(1.04); box-shadow: 0 0 0 7px rgba(0, 179, 134, 0); }
`;

function IntegrationIllustration({ open }) {
  if (!open) return null;

  return (
    <Box
      aria-label="Meon integrated with DigiLocker"
      sx={{
        mx: 1.75,
        mb: 2,
        p: 1.75,
        position: "relative",
        overflow: "hidden",
        borderRadius: "16px",
        border: "1px solid var(--dl-border)",
        background:
          "linear-gradient(155deg, var(--dl-surface) 0%, var(--dl-surface-soft) 58%, var(--dl-success-soft) 100%)",
        boxShadow: "0 10px 30px rgba(15, 23, 42, .07)",
        animation: `${contextEnter} .45s cubic-bezier(.2,.75,.25,1) both`,
        "@media (prefers-reduced-motion: reduce)": {
          animation: "none",
          "& *": { animation: "none !important" },
        },
        "&::before": {
          content: '""',
          position: "absolute",
          width: 120,
          height: 120,
          right: -58,
          bottom: -58,
          borderRadius: "50%",
          background: "rgba(0, 179, 134, .07)",
        },
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Box>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.65,
              color: "success.main",
              fontSize: 9,
              fontWeight: 800,
              letterSpacing: ".08em",
            }}
          >
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                bgcolor: "#0f9d86",
                animation: `${softPulse} 2.2s ease-in-out infinite`,
                "@media (prefers-reduced-motion: reduce)": { animation: "none" },
              }}
            />
            LIVE INTEGRATION
          </Box>
          <Box sx={{ color: "var(--dl-text-muted)", fontSize: 9.5, mt: 0.25 }}>
            Secure document infrastructure
          </Box>
        </Box>
        <VerifiedUserRoundedIcon sx={{ color: "#087765", fontSize: 20 }} />
      </Stack>

      <Stack alignItems="center" sx={{ mt: 1.5 }}>
        <Box
          sx={{
            width: "100%",
            height: 54,
            display: "grid",
            placeItems: "center",
            bgcolor: "var(--dl-surface)",
            border: "1px solid var(--dl-border)",
            borderRadius: "11px",
            boxShadow: "0 5px 13px rgba(15,23,42,.045)",
          }}
        >
          <Box
            component="img"
            src="/images/meon-logo.png"
            alt="Meon"
            sx={{ width: 104, height: "auto", display: "block" }}
          />
        </Box>

        <Box sx={{ height: 92, width: "100%", position: "relative" }}>
          <Box
            component="svg"
            viewBox="0 0 220 92"
            preserveAspectRatio="none"
            sx={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
          >
            <Box
              component="path"
              d="M42 0 C42 34 92 50 92 92"
              fill="none"
              stroke="#9fb1e8"
              strokeWidth="1.5"
              strokeDasharray="5 5"
              sx={{
                animation: `${dashFlow} 1.4s linear infinite`,
                "@media (prefers-reduced-motion: reduce)": { animation: "none" },
              }}
            />
            <Box
              component="path"
              d="M128 92 C128 55 178 38 178 0"
              fill="none"
              stroke="#82d7be"
              strokeWidth="1.5"
              strokeDasharray="5 5"
              sx={{
                animation: `${dashFlow} 1.4s linear reverse infinite`,
                "@media (prefers-reduced-motion: reduce)": { animation: "none" },
              }}
            />
          </Box>

          {[0, 1].map((index) => (
            <Box
              key={`request-${index}`}
              sx={{
                position: "absolute",
                left: 0,
                top: 0,
                width: 15,
                height: 11,
                borderRadius: "3px",
                bgcolor: "var(--dl-surface)",
                border: "1.5px solid #5672cf",
                boxShadow: "0 3px 8px rgba(49,84,186,.2)",
                offsetPath: 'path("M42 0 C42 34 92 50 92 92")',
                offsetRotate: "0deg",
                animation: `${packetFlow} 3s ease-in-out ${index * 1.5}s infinite`,
                "&::before, &::after": {
                  content: '""',
                  position: "absolute",
                  left: 3,
                  height: 1,
                  bgcolor: "#8da1df",
                },
                "&::before": { top: 3, width: 7 },
                "&::after": { top: 6, width: 5 },
                "@media (prefers-reduced-motion: reduce)": {
                  animation: "none",
                  left: 78,
                  top: 38,
                  opacity: index === 0 ? 1 : 0,
                },
              }}
            />
          ))}
          {[0, 1].map((index) => (
            <Box
              key={`response-${index}`}
              sx={{
                position: "absolute",
                left: 0,
                top: 0,
                width: 15,
                height: 11,
                borderRadius: "5px",
                bgcolor: "#0f9d86",
                border: "1.5px solid #fff",
                boxShadow: "0 3px 9px rgba(0,179,134,.25)",
                offsetPath: 'path("M128 92 C128 55 178 38 178 0")',
                offsetRotate: "0deg",
                animation: `${packetFlow} 3s ease-in-out ${index * 1.5}s infinite`,
                "&::before": {
                  content: '"✓"',
                  position: "absolute",
                  inset: 0,
                  display: "grid",
                  placeItems: "center",
                  color: "#fff",
                  fontSize: 7,
                  fontWeight: 900,
                },
                "@media (prefers-reduced-motion: reduce)": {
                  animation: "none",
                  left: 132,
                  top: 42,
                  opacity: index === 0 ? 1 : 0,
                },
              }}
            />
          ))}
          <Box
            sx={{
              position: "absolute",
              left: "50%",
              top: "50%",
              transform: "translate(-50%, -50%)",
              width: 40,
              height: 40,
              display: "grid",
              placeItems: "center",
              borderRadius: "50%",
              bgcolor: "var(--dl-surface)",
              border: "1px solid var(--dl-border)",
              color: "#3154ba",
              fontSize: 7,
              fontWeight: 800,
              zIndex: 1,
              animation: `${hubPulse} 2.4s ease-in-out infinite`,
              "&::before": {
                content: '""',
                position: "absolute",
                inset: -6,
                borderRadius: "50%",
                border: "1px dashed #9eafe0",
                transform: "translate(0, 0)",
                animation: `${rotateHub} 8s linear infinite`,
              },
              "@media (prefers-reduced-motion: reduce)": {
                animation: "none",
                "&::before": { animation: "none" },
              },
            }}
          >
            <Box textAlign="center" lineHeight={1.05}>
              API
              <Box component="span" display="block" color="#087765" fontSize={8}>
                ⇄
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              position: "absolute",
              left: 6,
              top: 34,
              color: "#6176bc",
              fontSize: 7,
              fontWeight: 750,
              writingMode: "vertical-rl",
              letterSpacing: ".06em",
            }}
          >
            REQUEST
          </Box>
          <Box
            sx={{
              position: "absolute",
              right: 5,
              top: 29,
              color: "#159875",
              fontSize: 7,
              fontWeight: 750,
              writingMode: "vertical-rl",
              letterSpacing: ".06em",
            }}
          >
            VERIFIED
          </Box>
        </Box>

        <Box
          sx={{
            width: "100%",
            height: 54,
            display: "grid",
            placeItems: "center",
            bgcolor: "var(--dl-surface)",
            border: "1px solid var(--dl-border)",
            borderRadius: "11px",
            boxShadow: "0 5px 13px rgba(15,23,42,.045)",
          }}
        >
          <Box
            component="img"
            src="/images/digilocker-logo.svg"
            alt="DigiLocker"
            sx={{ width: 132, height: "auto", display: "block" }}
          />
        </Box>
      </Stack>

      <Box sx={{ textAlign: "center", mt: 1.25, position: "relative" }}>
        <Box sx={{ color: "var(--dl-text)", fontSize: 10.5, fontWeight: 750 }}>
          Meon × DigiLocker
        </Box>
        <Box sx={{ color: "var(--dl-text-faint)", fontSize: 8.5, mt: 0.15 }}>
          Requests ⇄ Verified responses
        </Box>
      </Box>
    </Box>
  );
}

const contextVisuals = {
  reports: {
    eyebrow: "REPORT INTELLIGENCE",
    title: "Documents into insight",
    subtitle: "Filter · Analyse · Export",
    icon: "solar:chart-2-bold-duotone",
    color: "#522377",
    background:
      "linear-gradient(155deg, var(--dl-surface) 0%, var(--dl-surface-soft) 62%, var(--dl-primary-soft) 100%)",
  },
  recharge: {
    eyebrow: "CREDIT OPERATIONS",
    title: "Wallet recharge flow",
    subtitle: "Fund · Allocate · Retrieve",
    icon: "solar:wallet-money-bold-duotone",
    color: "#d97706",
    background:
      "linear-gradient(155deg, var(--dl-surface) 0%, var(--dl-warning-soft) 62%, var(--dl-warning-soft) 100%)",
  },
  credentials: {
    eyebrow: "SECURE ACCESS",
    title: "Protected API credentials",
    subtitle: "Authenticate · Authorize · Connect",
    icon: "solar:key-minimalistic-square-3-bold-duotone",
    color: "#2f6fa5",
    background:
      "linear-gradient(155deg, var(--dl-surface) 0%, var(--dl-info-soft) 58%, var(--dl-surface-soft) 100%)",
  },
};

function ContextIllustration({ type }) {
  const visual = contextVisuals[type];

  return (
    <Box
      key={type}
      sx={{
        mx: 1.75,
        mb: 2,
        p: 1.75,
        position: "relative",
        overflow: "hidden",
        borderRadius: "16px",
        border: "1px solid var(--dl-border)",
        background: visual.background,
        boxShadow: "0 10px 30px rgba(15,23,42,.07)",
        animation: `${contextEnter} .45s cubic-bezier(.2,.75,.25,1) both`,
        "@media (prefers-reduced-motion: reduce)": {
          animation: "none",
          "& *": { animation: "none !important" },
        },
        "&::after": {
          content: '""',
          position: "absolute",
          width: 110,
          height: 110,
          right: -55,
          bottom: -55,
          borderRadius: "50%",
          background: `${visual.color}12`,
        },
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Box>
          <Box
            sx={{
              color: visual.color,
              fontSize: 8.5,
              fontWeight: 850,
              letterSpacing: ".085em",
            }}
          >
            {visual.eyebrow}
          </Box>
          <Box sx={{ color: "var(--dl-text-muted)", fontSize: 9.25, mt: 0.25 }}>
            Context-aware workspace
          </Box>
        </Box>
        <Box
          sx={{
            width: 31,
            height: 31,
            display: "grid",
            placeItems: "center",
            borderRadius: "9px",
            bgcolor: "var(--dl-surface)",
            color: visual.color,
            border: "1px solid var(--dl-border)",
            boxShadow: "0 5px 12px rgba(15,23,42,.06)",
          }}
        >
          <Icon icon={visual.icon} width="19" />
        </Box>
      </Stack>

      {type === "reports" && (
        <Box sx={{ height: 142, mt: 1.25, position: "relative" }}>
          <Box
            sx={{
              position: "absolute",
              left: 14,
              top: 12,
              width: 62,
              height: 76,
              bgcolor: "var(--dl-surface)",
              border: "1px solid var(--dl-border)",
              borderRadius: "9px",
              boxShadow: "0 8px 18px rgba(79,70,229,.09)",
              animation: `${floatElement} 3s ease-in-out infinite`,
            }}
          >
            <Icon
              icon="solar:documents-bold-duotone"
              width="26"
              color="#522377"
              style={{ margin: "14px 0 4px 17px" }}
            />
            {[30, 40, 24].map((width) => (
              <Box key={width} sx={{ width, height: 3, bgcolor: "#e5e7f8", borderRadius: 2, mx: "auto", mt: 0.5 }} />
            ))}
          </Box>
          {[0, 1].map((index) => (
            <Box
              key={index}
              sx={{
                position: "absolute",
                left: "50%",
                top: 30,
                width: 14,
                height: 18,
                bgcolor: "var(--dl-surface)",
                border: "1px solid #818cf8",
                borderRadius: "3px",
                animation: `${reportTravel} 3s ease-in-out ${index * 1.5}s infinite`,
                "@media (prefers-reduced-motion: reduce)": { animation: "none", opacity: 0 },
              }}
            />
          ))}
          <Box
            sx={{
              position: "absolute",
              right: 9,
              bottom: 9,
              width: 92,
              height: 82,
              p: 1,
              bgcolor: "var(--dl-surface)",
              border: "1px solid var(--dl-border)",
              borderRadius: "10px",
              boxShadow: "0 8px 18px rgba(79,70,229,.09)",
            }}
          >
            <Box sx={{ fontSize: 7.5, color: "var(--dl-text-muted)", fontWeight: 750 }}>LIVE REPORT</Box>
            <Stack direction="row" alignItems="flex-end" spacing={0.55} height={52} mt={0.5}>
              {[38, 58, 30, 68, 48].map((height, index) => (
                <Box
                  key={height}
                  sx={{
                    flex: 1,
                    height: `${height}%`,
                    bgcolor: index === 3 ? "#522377" : "#c7d2fe",
                    borderRadius: "3px 3px 1px 1px",
                    transformOrigin: "bottom",
                    animation: `${barRise} 2.2s ease-in-out ${index * 0.12}s infinite alternate`,
                  }}
                />
              ))}
            </Stack>
          </Box>
        </Box>
      )}

      {type === "recharge" && (
        <Box sx={{ height: 142, mt: 1.25, position: "relative", display: "grid", placeItems: "center" }}>
          {[0, 1, 2].map((index) => (
            <Box
              key={index}
              sx={{
                position: "absolute",
                top: 0,
                left: `${34 + index * 16}%`,
                width: 19,
                height: 19,
                borderRadius: "50%",
                display: "grid",
                placeItems: "center",
                bgcolor: "#d88a13",
                color: "#fff",
                fontSize: 9,
                fontWeight: 900,
                boxShadow: "0 4px 9px rgba(245,158,11,.25)",
                animation: `${coinDrop} 2.8s ease-in ${index * 0.8}s infinite`,
              }}
            >
              ₹
            </Box>
          ))}
          <Box
            sx={{
              width: 150,
              height: 76,
              mt: 4.5,
              p: 1.5,
              borderRadius: "13px",
              color: "#fff",
              background: "linear-gradient(135deg, #a86408, #d88a13)",
              boxShadow: "0 14px 24px rgba(217,119,6,.2)",
            }}
          >
            <Stack direction="row" justifyContent="space-between">
              <Icon icon="solar:wallet-bold-duotone" width="22" />
              <Box sx={{ fontSize: 7.5, opacity: 0.78 }}>WALLET</Box>
            </Stack>
            <Box sx={{ fontSize: 8, opacity: 0.7, mt: 1 }}>AVAILABLE CREDITS</Box>
            <Box sx={{ fontSize: 18, fontWeight: 800, lineHeight: 1 }}>••••</Box>
          </Box>
        </Box>
      )}

      {type === "credentials" && (
        <Box sx={{ height: 142, mt: 1.25, position: "relative", display: "grid", placeItems: "center" }}>
          <Box
            sx={{
              width: 154,
              height: 94,
              display: "grid",
              placeItems: "center",
              bgcolor: "var(--dl-surface)",
              border: "1px solid var(--dl-border)",
              borderRadius: "14px",
              boxShadow: "0 12px 25px rgba(2,132,199,.1)",
            }}
          >
            <Box
              sx={{
                width: 55,
                height: 55,
                display: "grid",
                placeItems: "center",
                borderRadius: "50%",
                color: "#2f6fa5",
                bgcolor: "#eff9ff",
                border: "1px dashed #7dd3fc",
                animation: `${floatElement} 2.8s ease-in-out infinite`,
              }}
            >
              <Icon icon="solar:key-minimalistic-bold-duotone" width="28" />
            </Box>
          </Box>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              mt: -2,
              ml: -7,
              width: 14,
              height: 4,
              borderRadius: 3,
              bgcolor: "#38bdf8",
              boxShadow: "0 0 10px rgba(56,189,248,.65)",
              animation: `${secureSweep} 2.5s ease-in-out infinite`,
            }}
          />
          <Box sx={{ position: "absolute", bottom: 9, color: "#0369a1", fontSize: 8, fontWeight: 800 }}>
            TOKEN ENCRYPTED · ACCESS VERIFIED
          </Box>
        </Box>
      )}

      <Box sx={{ textAlign: "center", position: "relative", zIndex: 1 }}>
        <Box sx={{ color: "var(--dl-text)", fontSize: 10.5, fontWeight: 750 }}>
          {visual.title}
        </Box>
        <Box sx={{ color: "var(--dl-text-faint)", fontSize: 8.5, mt: 0.2 }}>
          {visual.subtitle}
        </Box>
      </Box>
    </Box>
  );
}

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  minHeight: topbarHeight,
  padding: theme.spacing(0, 1.5),

  ...theme.mixins.toolbar,
}));

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  height: topbarHeight,
  color: "var(--dl-text)",
  background: "rgba(255,255,255,.94)",
  backdropFilter: "blur(14px)",
  borderBottom: "1px solid var(--dl-border)",
  boxShadow: "0 4px 18px rgba(15,23,42,.035)",
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  "& .MuiDrawer-paper": {
    borderRight: "1px solid var(--dl-border)",
    background:
      "linear-gradient(180deg, var(--dl-surface) 0%, var(--dl-surface-soft) 100%)",
    boxShadow: "4px 0 22px rgba(15,23,42,.035)",
  },
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));

export default function NavSection() {
  const location = useLocation();
  const [open, setOpen] = useState("");
  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    function handleResize() {
      setOpen(window.innerWidth < 786 ? false : true);
    }

    window.addEventListener("resize", handleResize);
    handleResize(); // Call handleResize initially to set the initial screen size
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const illustrationType = location.pathname.startsWith("/reports")
    ? "reports"
    : location.pathname.startsWith("/client-history")
    ? "recharge"
    : location.pathname.startsWith("/credentials")
    ? "credentials"
    : "dashboard";

  const pageContext = {
    dashboard: {
      title: "Dashboard",
      subtitle: "Business performance overview",
      icon: "solar:widget-5-bold-duotone",
    },
    reports: {
      title: "Reports",
      subtitle: "Transactions and fetched documents",
      icon: "solar:chart-square-bold-duotone",
    },
    recharge: {
      title: "Recharge History",
      subtitle: "Wallet funding and credit activity",
      icon: "solar:wallet-money-bold-duotone",
    },
    credentials: {
      title: "Credentials",
      subtitle: "Secure API access configuration",
      icon: "solar:key-minimalistic-square-3-bold-duotone",
    },
  }[illustrationType];

  return (
    <Box>
      <CssBaseline />
      <AppBar
        position="fixed"
        open={open}
        className="header"
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={{
            xs: 0.5,
            sm: 2,
          }}
          justifyContent="space-between"
          sx={{ height: "100%", px: { xs: 1.5, sm: 2.5, lg: 3 } }}
        >
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <IconButton
              aria-label="open drawer"
              onClick={handleDrawerOpen}
              edge="start"
              sx={{
                width: 38,
                height: 38,
                color: "var(--dl-text-secondary)",
                border: "1px solid var(--dl-border)",
                borderRadius: "10px",
                ...(open && { display: "none" }),
              }}
            >
              <MenuIcon />
            </IconButton>
            <Box
              sx={{
                width: 35,
                height: 35,
                display: { xs: "none", sm: "grid" },
                placeItems: "center",
                color: "primary.main",
                bgcolor: "var(--dl-primary-soft)",
                borderRadius: "9px",
              }}
            >
              <Icon icon={pageContext.icon} width="19" />
            </Box>
            <Box>
              <Box sx={{ color: "var(--dl-text)", fontSize: 13.5, fontWeight: 780 }}>
                {pageContext.title}
              </Box>
              <Box
                sx={{
                  display: { xs: "none", sm: "block" },
                  color: "var(--dl-text-faint)",
                  fontSize: 10.5,
                }}
              >
                {pageContext.subtitle}
              </Box>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                display: { xs: "none", md: "flex" },
                alignItems: "center",
                gap: 0.65,
                px: 1.1,
                py: 0.55,
                color: "success.main",
                bgcolor: "var(--dl-success-soft)",
                border: "1px solid var(--dl-border)",
                borderRadius: "20px",
                fontSize: 9.5,
                fontWeight: 750,
              }}
            >
              <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#0f9d86" }} />
              Secure connection
            </Box>
            <ColorModeToggle />
            <AccountPopover />
          </Stack>
        </Stack>
      </AppBar>
      {/* User Admin Navbar End*/}

      <Drawer variant="permanent" className="navBar" open={open}>
        {/* User Admin Sidebar Left Header*/}
        <DrawerHeader
          sx={{
            display: "flex",
            justifyContent: open ? "space-between" : "center",
            borderBottom: "1px solid var(--dl-border)",
          }}
        >
          {open && (
            <Box
              sx={{
                pl: 0.5,
                display: "flex",
                alignItems: "center",
              }}
            >
              <Box
                component="img"
              src="/images/meon-logo.png"
                alt="Meon Technologies"
                sx={{ width: 138, height: "auto", display: "block" }}
              />
            </Box>
          )}
          <IconButton
            onClick={open ? handleDrawerClose : handleDrawerOpen}
            sx={{
              width: 34,
              height: 34,
              color: "var(--dl-text-muted)",
              border: "1px solid var(--dl-border)",
              borderRadius: "9px",
              bgcolor: "var(--dl-surface)",
              "&:hover": { bgcolor: "var(--dl-surface-muted)" },
            }}
          >
            {open ? (
              <ChevronLeftIcon />
            ) : (
              <ChevronRightIcon />
            )}
          </IconButton>
        </DrawerHeader>
        {/* User Admin Sidebar Left Header End*/}

        <Divider />

        {/* User Admin sidebar */}
        <List sx={{ flexGrow: 1, px: open ? 1.25 : 0.75, pt: 1.5 }}>
          {navConfig.map((item, index) => (
            <ListItem
              component={RouterLink}
              to={item.path}
              key={item.id}
              disablePadding
              sx={{
                display: "block",
                color: "var(--dl-text-secondary)",
                mb: 0.5,
                borderRadius: "10px",
                "&.active": {
                  color: "primary.light",
                  fontWeight: 750,
                  background:
                    "linear-gradient(90deg, var(--dl-primary-soft), var(--dl-surface-soft))",
                  boxShadow: "inset 3px 0 0 var(--dl-purple)",
                },
                "&:hover": { bgcolor: "var(--dl-surface-muted)" },
              }}
            >
              <ListItemButton
                sx={{
                  minHeight: 46,
                  justifyContent: open ? "initial" : "center",
                  px: open ? 1.65 : 1,
                  borderRadius: "10px",
                  "&:hover": {
                    backgroundColor: "transparent", // Removes hover background color
                  },
                }}
              >
                <StyledNavItemIcon>{item.icon && item.icon}</StyledNavItemIcon>
                {/* </ListItemIcon> */}
                <ListItemText
                  disableTypography
                  primary={item.title && item.title}
                  sx={{
                    opacity: open ? 1 : 0,
                    "& .MuiTypography-root": { fontSize: 13.25 },
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
        {/* User Admin Sidebar End*/}

        {open &&
          (illustrationType === "dashboard" ? (
            <IntegrationIllustration key="dashboard" open={open} />
          ) : (
            <ContextIllustration key={illustrationType} type={illustrationType} />
          ))}

        {/* <List
          sx={{
            alignSelf: "flex-end",
            justifyContent: "center",
            width: "100%",
            
          }}
        > 
          {navProfile.map((item, index) => (
            <ListItem
              component={RouterLink}
              // to={item.path}
              key={item.id}
              disablePadding
              sx={
                {
                  // display: "block",
                  // color: "black",
                  // "&.active": {
                  //   color: "#522377",
                  //   fontWeight: "bold",
                  //   bgcolor: "#F6FAFF",
                  //   borderLeft: "2px solid",
                  // },
                }
              }
            >
              <Divider />
              <ListItemButton
                sx={{
                  minHeight: 48,
                  justifyContent: open ? "initial" : "center",
                  px: 2.5,
                }}
              >
                {/* <StyledNavItemIcon>{item.icon && item.icon}</StyledNavItemIcon> */}
        {/* </ListItemIcon> */}
        {/* <ListItemText
                  disableTypography
                  primary={item.title && item.title}
                  sx={{ opacity: open ? 1 : 0 }}
                /> */}
        {/* </ListItemButton> */}
        {/* </ListItem> */}
        {/* ))} */}
        {/* </List> */}
      </Drawer>
    </Box>
  );
}
